/**
 * File: leadAudit.ts
 * Path: packages/config/src/leads/leadAudit.ts
 * Description: The website audit kept on a lead, and the email that sends it.
 *
 * When staff audit a lead's site from the lead's page, the server keeps what
 * the report found on the lead (not the PDF): the score, the top problems and
 * the score per area. The lead page shows it, and the email draft is written
 * from it, so the email names the same problems the attached PDF does.
 *
 * The email is the first approach to a business that never asked to hear from
 * us, so it follows the same POPIA rule as the WhatsApp opener (model/opener.ts
 * in the admin): one approach without consent, an opt-out in every one, and
 * never to anyone marked "do not contact".
 */

import { messageBlock } from "../consent/consent";
import { BUSINESS_CONTACT } from "../voice/contact";
import type { Lead } from "./leads";

/** One problem the audit found, as the report words it for the owner. */
export interface LeadAuditProblem {
  title: string;
  /** What it costs the owner, in plain words. */
  impact: string;
  priority: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
}

/** What the last audit of the lead's website found. */
export interface LeadAudit {
  /** The reference printed on the PDF (AUD-...). */
  auditId: string;
  domain: string;
  /** 0 to 100, over the checks that ran. */
  score: number;
  /** The report's one-line verdict. */
  headline: string;
  /** Worst first, as on the report's first page. At most LEAD_AUDIT_PROBLEMS. */
  problems: LeadAuditProblem[];
  /** Score per area that was measured, e.g. "Speed" 42. */
  areas: Array<{ name: string; score: number }>;
  /** The design check's summary, when it ran. */
  designSummary: string;
  /** The owner's own figures, when staff typed them in. */
  averageJobValue: number | null;
  monthlyVisitors: number | null;
  /** Who ran it. */
  byEmail: string;
  ranAt: number | null;
}

/** How many problems are kept on the lead: the report's top list. */
export const LEAD_AUDIT_PROBLEMS = 5;

const PRIORITIES: readonly LeadAuditProblem["priority"][] = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

const str = (value: unknown, max = 600): string =>
  typeof value === "string" ? value.trim().slice(0, max) : "";

const positiveOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;

const millis = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value && typeof value === "object") {
    const ts = value as { toMillis?: unknown; seconds?: unknown };
    if (typeof ts.toMillis === "function") return (ts.toMillis as () => number)();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
  }
  return null;
};

/** Reads a stored audit, or null when there is none worth showing. */
export const toLeadAudit = (value: unknown): LeadAudit | null => {
  if (!value || typeof value !== "object") return null;
  const a = value as Record<string, unknown>;
  const score = a.score;
  const domain = str(a.domain, 253);
  if (typeof score !== "number" || !Number.isFinite(score) || !domain) return null;

  const problems = (Array.isArray(a.problems) ? a.problems : [])
    .map((p): LeadAuditProblem | null => {
      if (!p || typeof p !== "object") return null;
      const row = p as Record<string, unknown>;
      const title = str(row.title, 200);
      if (!title) return null;
      return {
        title,
        impact: str(row.impact),
        priority: PRIORITIES.find((x) => x === row.priority) ?? "MEDIUM",
      };
    })
    .filter((p): p is LeadAuditProblem => p !== null)
    .slice(0, LEAD_AUDIT_PROBLEMS);

  const areas = (Array.isArray(a.areas) ? a.areas : [])
    .map((r) => {
      const row = (r ?? {}) as Record<string, unknown>;
      const name = str(row.name, 80);
      return name && typeof row.score === "number" && Number.isFinite(row.score)
        ? { name, score: Math.round(row.score) }
        : null;
    })
    .filter((r): r is { name: string; score: number } => r !== null);

  return {
    auditId: str(a.auditId, 80),
    domain,
    score: Math.max(0, Math.min(100, Math.round(score))),
    headline: str(a.headline, 300),
    problems,
    areas,
    designSummary: str(a.designSummary, 1000),
    averageJobValue: positiveOrNull(a.averageJobValue),
    monthlyVisitors: positiveOrNull(a.monthlyVisitors),
    byEmail: str(a.byEmail, 200),
    ranAt: millis(a.ranAt),
  };
};

// ─── The email that sends the audit (callable `draftLeadEmail`) ───────────

export interface DraftLeadEmailRequest {
  leadId: string;
}

export interface DraftLeadEmailResponse {
  subject: string;
  /** The whole email: greeting, message, sign-off and the opt-out line. */
  body: string;
  /** "ai" when Gemini wrote it; "template" when it couldn't, with why. */
  source: "ai" | "template";
  reason?: string;
}

/** Days after the email that the follow-up call is set for (Vusi #4840: call to check it arrived). */
export const AUDIT_EMAIL_FOLLOW_UP_DAYS = 1;

/** POPIA: every unsolicited approach says how to stop them. */
export const EMAIL_OPT_OUT_LINE =
  "If you'd rather not hear from me, reply \"stop\" and I won't email again.";

export const firstNameOf = (name: string): string => name.trim().split(/\s+/)[0] ?? "";

/** Name, business, number and site under every email. */
export const emailSignature = (senderName: string): string =>
  [
    firstNameOf(senderName) || "Liam",
    "IDK Solutions",
    `${BUSINESS_CONTACT.phone} (call or WhatsApp)`,
    "idksolutions.co.za",
  ].join("\n");

/**
 * Puts the greeting, sign-off and opt-out around the message, so whoever
 * wrote the middle (Gemini or the template) can't leave them out or word the
 * opt-out away.
 */
export const wrapEmailBody = (
  message: string,
  lead: Pick<Lead, "contactName">,
  senderName: string,
): string =>
  [
    `Hi ${firstNameOf(lead.contactName) || "there"},`,
    message.trim(),
    emailSignature(senderName),
    EMAIL_OPT_OUT_LINE,
  ].join("\n\n");

/** The report's problem title as it reads mid-sentence: "No booking button..." → "no booking button...". */
const midSentence = (title: string): string => {
  const t = title.trim().replace(/\.$/, "");
  // Keep acronyms and names ("SSL", "Google") as they are.
  return /^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t;
};

/**
 * The email without AI: the same rules, filled in from the audit. Used when
 * Gemini is unavailable, so the button always gives something worth sending.
 */
export const auditEmailTemplate = (
  lead: Pick<Lead, "name" | "contactName">,
  audit: Pick<LeadAudit, "domain" | "score" | "problems">,
  senderName: string,
): { subject: string; body: string } => {
  const [first, second] = audit.problems;
  const found = first
    ? second
      ? `The two that stood out: ${midSentence(first.title)}, and ${midSentence(second.title)}.`
      : `The one that stood out: ${midSentence(first.title)}.`
    : "";
  const cost = first?.impact ? first.impact.trim() : "";
  const message = [
    `I ran a check-up on ${audit.domain} and it scored ${audit.score} out of 100. ${found}`.trim(),
    cost,
    "The full report is attached, with what to fix first. It's yours to keep, whether you use me or not.",
    "If you'd like it fixed, I do it for one fixed price, and you get more enquiries within 60 days or I keep working for free. Want me to send you that price?",
  ]
    .filter(Boolean)
    .join("\n\n");

  return {
    subject: `${lead.name}: your website check-up`.slice(0, 90),
    body: wrapEmailBody(message, lead, senderName),
  };
};

export type AuditEmailBlock =
  | "do-not-contact"
  | "no-email"
  | "no-audit"
  | "already-approached"
  | null;

/** Why the audit email can't be written now, or null when it can. */
export const auditEmailBlock = (
  lead: Pick<Lead, "email" | "doNotContact" | "openerSentAt" | "marketingConsent" | "audit" | "consent">,
): AuditEmailBlock => {
  const consent = messageBlock(lead.consent);
  if (lead.doNotContact || consent === "stopped") return "do-not-contact";
  if (!lead.audit) return "no-audit";
  if (!lead.email) return "no-email";
  if (consent === "already-asked" || (lead.openerSentAt !== null && !lead.marketingConsent && lead.consent.state !== "existing_client"))
    return "already-approached";
  return null;
};

/** Gmail's compose window with the email filled in (Gmail links can't attach files). */
export const gmailComposeLink = (to: string, subject: string, body: string): string =>
  `https://mail.google.com/mail/?${new URLSearchParams({ view: "cm", fs: "1", to, su: subject, body }).toString()}`;

/** The computer's own email app, filled in. */
export const mailtoLink = (to: string, subject: string, body: string): string =>
  `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
